import { useState, useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Search, X, Globe, MapPin, Loader2, Heart, ChevronLeft, ChevronRight, ChevronDown, Store } from "lucide-react";
import { SocialMediaIcons } from "@/components/SocialMediaIcons";
import { formatHoursDisplay } from "@/components/HoursSelector";
import { useAuth } from "@/contexts/AuthContext";
import { normalizeUrl } from "@/lib/urlUtils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAGE_SIZE = 9;

const DEFAULT_BG_IMAGE = "https://customer-assets.emergentagent.com/job_biz-directory-27/artifacts/miycx0rf_614shoplocal-background.png";

const LOCATION_OPTIONS = [
  "Clintonville", "Columbus", "East Market", "German Village", "Hilliard",
  "Linden", "New Albany", "Reynoldsburg", "Short North", "Westerville", "Worthington",
  "Outside of the Greater Columbus Area"
];

const TYPE_OPTIONS = [
  "Goods", "Services", "Food & Beverage", "Nonprofits"
];

const CHIP_THEMES = [
  {
    selected: "border-[#42D78A] bg-[#42D78A] text-[#1a1a1a]",
    idle: "border-[#42D78A]/35 bg-[#42D78A]/10 text-[#157347] hover:border-[#42D78A] hover:bg-[#42D78A]/20",
  },
  {
    selected: "border-[#AFC8FF] bg-[#AFC8FF] text-[#1a1a1a]",
    idle: "border-[#AFC8FF]/70 bg-[#AFC8FF]/20 text-[#36558F] hover:border-[#AFC8FF] hover:bg-[#AFC8FF]/35",
  },
  {
    selected: "border-[#FFE8A3] bg-[#FFE8A3] text-[#1a1a1a]",
    idle: "border-[#FFE8A3] bg-[#FFE8A3]/35 text-[#7A5B00] hover:border-[#F2C94C] hover:bg-[#FFE8A3]/60",
  },
  {
    selected: "border-[#F6B6D5] bg-[#F6B6D5] text-[#1a1a1a]",
    idle: "border-[#F6B6D5]/70 bg-[#F6B6D5]/20 text-[#8A2F5A] hover:border-[#F6B6D5] hover:bg-[#F6B6D5]/35",
  },
];

const getChipClasses = (selected, index) => {
  const theme = CHIP_THEMES[index % CHIP_THEMES.length];
  return `px-3 py-1.5 text-xs font-semibold border rounded-full transition-all shadow-sm ${
    selected ? "border-[#1a1a1a] bg-[#1a1a1a] text-white" : theme.idle
  }`;
};

const FilterGroup = ({ title, open, onToggle, count, children }) => (
  <div className="mb-8">
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full items-center justify-between gap-3 text-left"
      aria-expanded={open}
    >
      <span className="min-w-0 text-[13px] font-bold text-[#1a1a1a] uppercase tracking-wide" style={{ fontFamily: "'Outfit', sans-serif" }}>
        {title}
      </span>
      <span className="flex items-center gap-2">
        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-bold text-gray-500">{count}</span>
        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </span>
    </button>
    {open && <div className="mt-3 flex flex-wrap gap-2">{children}</div>}
  </div>
);

const getLogoUrl = (logo) => {
  if (!logo) return null;
  if (logo.startsWith("http") || logo.startsWith("data:") || logo.startsWith("blob:")) return logo;
  const baseUrl = BACKEND_URL || window.location.origin;
  try {
    return new URL(logo, baseUrl).toString();
  } catch {
    return logo.startsWith("/") ? `${baseUrl.replace(/\/$/, "")}${logo}` : logo;
  }
};

const formatDirectoryHours = (hours) => {
  const displayHours = typeof hours === "object" ? formatHoursDisplay(hours) : hours;
  return displayHours?.replace(/,\s*/g, "\n") || "";
};

const getBusinessSubcategories = (business) => {
  const categories = Array.isArray(business?.business_categories) ? business.business_categories : [];
  const visible = categories.filter((category) => category !== "Other");
  const customCategory = business?.business_category_other?.trim();
  if (customCategory) {
    visible.push(customCategory);
  }
  if (visible.length === 0 && business?.category && business.category !== business.business_type) {
    visible.push(business.category);
  }
  return Array.from(new Set(visible));
};

const getBusinessLocations = (business) => {
  if (Array.isArray(business?.business_locations) && business.business_locations.length > 0) {
    return business.business_locations.filter(Boolean);
  }
  return business?.location ? [business.location] : [];
};

const getBusinessAddresses = (business) => {
  if (Array.isArray(business?.business_addresses) && business.business_addresses.length > 0) {
    return business.business_addresses.filter(Boolean);
  }
  return business?.address ? [business.address] : [];
};

const getBusinessLocationEntries = (business) => {
  const locations = getBusinessLocations(business);
  const addresses = business?.is_home_based ? [] : getBusinessAddresses(business);
  const additionalLocations = business?.is_home_based || !Array.isArray(business?.additional_business_locations)
    ? []
    : business.additional_business_locations
      .filter((entry) => entry?.name || entry?.address)
      .map((entry) => ({
        location: entry.name || "Additional Location",
        address: entry.address || "",
      }));
  if (locations.length === 0 && addresses.length === 0 && additionalLocations.length === 0) return [];
  const length = Math.max(locations.length, addresses.length);
  const primaryEntries = Array.from({ length }, (_, index) => ({
    location: locations[index] || locations[0] || "",
    address: addresses[index] || "",
  })).filter((entry) => entry.location || entry.address);
  return [...primaryEntries, ...additionalLocations];
};

const getMapQuery = (entry) => [entry.address, entry.location].filter(Boolean).join(", ");

const getDefaultLocationIndex = (business) => {
  const entries = getBusinessLocationEntries(business);
  const addressIndex = entries.findIndex((entry) => entry.address);
  return addressIndex >= 0 ? addressIndex : null;
};

const formatMarketSeason = (market) => {
  if (market?.season_year_round) return "Year-round";
  const fmt = (dateValue) => {
    if (!dateValue) return "";
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return dateValue;
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };
  const start = fmt(market?.season_start);
  const end = fmt(market?.season_end);
  if (start && end) return `${start} - ${end}`;
  return start || end || "";
};

const filtersDefaultOpen = () => (
  typeof window === "undefined" || window.matchMedia("(min-width: 768px)").matches
);

const DirectoryPage = () => {
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [savedBusinessIds, setSavedBusinessIds] = useState([]);
  const [openFilters, setOpenFilters] = useState(() => {
    const open = filtersDefaultOpen();
    return { location: open, type: open, subcategory: open };
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, user } = useAuth();
  const isShopper = user?.userType === "shopper";

  const toggleFilterGroup = (group) => {
    setOpenFilters((previous) => ({ ...previous, [group]: !previous[group] }));
  };

  useEffect(() => {
    const fetchBusinesses = async () => {
      try {
        const response = await axios.get(`${API}/directory`);
        setBusinesses(response.data);
      } catch (error) {
        console.error("Failed to fetch businesses:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchBusinesses();
  }, []);

  useEffect(() => {
    const fetchShopper = async () => {
      if (!isShopper || !user?.token) return;
      try {
        const response = await axios.get(`${API}/shopper`, { headers: { Authorization: `Bearer ${user.token}` } });
        setSavedBusinessIds(response.data.saved_business_ids || []);
      } catch {
        setSavedBusinessIds([]);
      }
    };
    fetchShopper();
  }, [isShopper, user?.token]);

  useEffect(() => {
    if (loading) return;
    const businessId = searchParams.get("business");
    if (!businessId) {
      if (selectedBusiness) setSelectedBusiness(null);
      return;
    }
    const business = businesses.find((item) => item.id === businessId);
    if (business && selectedBusiness?.id !== businessId) {
      setSelectedBusiness(business);
    }
  }, [businesses, loading, searchParams, selectedBusiness?.id]);

  const openBusinessModal = (business) => {
    setSelectedBusiness(business);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("business", business.id);
    setSearchParams(nextParams);
  };

  const closeBusinessModal = () => {
    setSelectedBusiness(null);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("business");
    setSearchParams(nextParams);
  };

  const handleFollowBusiness = async (businessId) => {
    if (!isAuthenticated) {
      window.location.href = "/register";
      return;
    }
    if (!isShopper) {
      toast.error("Use a shopper profile to save businesses.");
      return;
    }

    try {
      const response = await axios.post(`${API}/shopper/follow/business/${businessId}`, {}, { headers: { Authorization: `Bearer ${user.token}` } });
      setSavedBusinessIds(response.data.saved_business_ids || []);
      toast.success(response.data.following ? "Business saved." : "Business removed.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update saved business");
    }
  };

  const filteredBusinesses = useMemo(() => {
    return businesses.filter((biz) => {
      const subcategories = getBusinessSubcategories(biz);
      const search = searchTerm.toLowerCase();
      const matchesSearch = !searchTerm ||
        biz.business_name?.toLowerCase().includes(search) ||
        biz.business_description?.toLowerCase().includes(search) ||
        biz.business_type?.toLowerCase().includes(search) ||
        getBusinessLocations(biz).some((location) => location.toLowerCase().includes(search)) ||
        subcategories.some((category) => category.toLowerCase().includes(search));
      const matchesLocation = !selectedLocation ||
        getBusinessLocations(biz).some((location) => location.toLowerCase().includes(selectedLocation.toLowerCase()));
      const matchesType = !selectedType ||
        biz.business_type?.toLowerCase() === selectedType.toLowerCase();
      const matchesSubcategory = !selectedSubcategory ||
        subcategories.some((category) => category.toLowerCase() === selectedSubcategory.toLowerCase());
      return matchesSearch && matchesLocation && matchesType && matchesSubcategory;
    }).sort((a, b) => (
      (a.business_name || "").localeCompare(b.business_name || "", undefined, { sensitivity: "base" })
    ));
  }, [businesses, searchTerm, selectedLocation, selectedType, selectedSubcategory]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedLocation, selectedType, selectedSubcategory]);

  const subcategoryOptions = useMemo(() => {
    const categories = new Set();
    businesses.forEach((biz) => {
      if (selectedType && biz.business_type?.toLowerCase() !== selectedType.toLowerCase()) return;
      getBusinessSubcategories(biz).forEach((category) => categories.add(category));
    });
    return Array.from(categories).sort((a, b) => a.localeCompare(b));
  }, [businesses, selectedType]);

  const locationOptions = useMemo(() => {
    const locations = new Set(LOCATION_OPTIONS);
    businesses.forEach((biz) => {
      getBusinessLocations(biz).forEach((location) => locations.add(location));
    });
    return Array.from(locations);
  }, [businesses]);

  const pageCount = Math.max(1, Math.ceil(filteredBusinesses.length / PAGE_SIZE));
  const currentSafePage = Math.min(currentPage, pageCount);
  const visibleBusinesses = filteredBusinesses.slice(
    (currentSafePage - 1) * PAGE_SIZE,
    currentSafePage * PAGE_SIZE
  );

  const clearFilters = () => {
    setSelectedLocation(null);
    setSelectedType(null);
    setSelectedSubcategory(null);
    setSearchTerm("");
    setCurrentPage(1);
  };

  return (
    <div className="page-fade-in">
      {/* Hero */}
      <section className="bg-[#EEF8F2] text-[#1a1a1a] border-b border-[#42D78A]/20 py-16 md:py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-[#42D78A] text-sm tracking-[0.2em] uppercase mb-4 font-medium" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Directory
          </p>
          <h1 className="text-4xl sm:text-5xl font-bold mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Business Directory
          </h1>
          <p className="text-gray-600 text-sm tracking-[0.15em] uppercase" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Discover local shops & vendors in Columbus
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/markets">
              <Button
                className="bg-[#42D78A] hover:bg-[#3bc27c] text-[#1a1a1a] rounded-full px-8 py-5 text-sm font-bold transition-all hover:-translate-y-0.5"
                style={{ fontFamily: "'Outfit', sans-serif" }}
                data-testid="business-directory-market-link"
              >
                View Market Directory
              </Button>
            </Link>
            <Link to="/events">
              <Button
                variant="outline"
                className="bg-white hover:bg-white text-[#1a1a1a] border-[#1a1a1a]/10 rounded-full px-8 py-5 text-sm font-bold transition-all hover:-translate-y-0.5 shadow-sm"
                style={{ fontFamily: "'Outfit', sans-serif" }}
                data-testid="business-directory-events-link"
              >
                See Upcoming Events
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="max-w-7xl mx-auto px-6 py-10">
        {/* Search */}
        <div className="relative mb-8 max-w-2xl mx-auto">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            type="text"
            placeholder="Search businesses..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-12 h-12 border-gray-200 rounded-full text-base shadow-sm"
            data-testid="search-input"
          />
        </div>

        <div className="flex flex-col md:flex-row gap-10">
          {/* Sidebar Filters */}
          <aside className="w-full md:w-52 md:max-w-52 flex-shrink-0">
            <FilterGroup
              title="Location"
              count={locationOptions.length}
              open={openFilters.location}
              onToggle={() => toggleFilterGroup("location")}
            >
              {locationOptions.map((location, index) => (
                <button
                  key={location}
                  onClick={() => setSelectedLocation(selectedLocation === location ? null : location)}
                  className={getChipClasses(selectedLocation === location, index)}
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                  data-testid={`location-filter-${location.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {location}
                </button>
              ))}
            </FilterGroup>

            <FilterGroup
              title="Main Category"
              count={TYPE_OPTIONS.length}
              open={openFilters.type}
              onToggle={() => toggleFilterGroup("type")}
            >
              {TYPE_OPTIONS.map((type, index) => (
                <button
                  key={type}
                  onClick={() => {
                    setSelectedType(selectedType === type ? null : type);
                    setSelectedSubcategory(null);
                  }}
                  className={getChipClasses(selectedType === type, index + 1)}
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                  data-testid={`type-filter-${type.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {type}
                </button>
              ))}
            </FilterGroup>

            {subcategoryOptions.length > 0 && (
              <FilterGroup
                title="Subcategory"
                count={subcategoryOptions.length}
                open={openFilters.subcategory}
                onToggle={() => toggleFilterGroup("subcategory")}
              >
                {subcategoryOptions.map((category, index) => (
                  <button
                    key={category}
                    onClick={() => setSelectedSubcategory(selectedSubcategory === category ? null : category)}
                    className={getChipClasses(selectedSubcategory === category, index + 2)}
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                    data-testid={`subcategory-filter-${category.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  >
                    {category}
                  </button>
                ))}
              </FilterGroup>
            )}

            {(selectedLocation || selectedType || selectedSubcategory || searchTerm) && (
              <Button
                variant="ghost"
                onClick={clearFilters}
                className="text-xs text-gray-500 hover:text-[#1a1a1a] p-0 font-medium"
                data-testid="clear-filters-btn"
              >
                Clear all filters
              </Button>
            )}
          </aside>

          {/* Business Grid */}
          <div className="flex-1">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
              </div>
            ) : filteredBusinesses.length === 0 ? (
              <div className="text-center py-20 text-gray-500" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                No businesses found matching your criteria.
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {visibleBusinesses.map((business) => (
                    <BusinessCard
                      key={business.id}
                      business={business}
                      onClick={() => openBusinessModal(business)}
                      isSaved={savedBusinessIds.includes(business.id)}
                      onFollow={handleFollowBusiness}
                    />
                  ))}
                </div>

                {pageCount > 1 && (
                  <div className="mt-10 flex items-center justify-center gap-3" data-testid="business-pagination">
                    <Button
                      variant="outline"
                      onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                      disabled={currentSafePage === 1}
                      className="h-11 w-11 rounded-full border-2 border-[#1a1a1a] text-[#1a1a1a] hover:bg-[#1a1a1a] hover:text-white disabled:opacity-40"
                      data-testid="business-prev-page-btn"
                      aria-label="Previous business page"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span
                      className="min-w-28 text-center text-sm font-semibold text-gray-600"
                      style={{ fontFamily: "'Montserrat', sans-serif" }}
                      data-testid="business-page-count"
                    >
                      Page {currentSafePage} of {pageCount}
                    </span>
                    <Button
                      variant="outline"
                      onClick={() => setCurrentPage((page) => Math.min(pageCount, page + 1))}
                      disabled={currentSafePage === pageCount}
                      className="h-11 w-11 rounded-full border-2 border-[#1a1a1a] text-[#1a1a1a] hover:bg-[#1a1a1a] hover:text-white disabled:opacity-40"
                      data-testid="business-next-page-btn"
                      aria-label="Next business page"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center py-16 border-t border-gray-100 mt-12">
          <h2 className="text-2xl font-bold mb-5 text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Not seeing your business?
          </h2>
          <Button
            className="bg-[#42D78A] hover:bg-[#3bc27c] text-[#1a1a1a] rounded-full px-10 py-5 font-bold transition-all hover:-translate-y-0.5"
            onClick={() => window.location.href = "/register"}
            style={{ fontFamily: "'Outfit', sans-serif" }}
            data-testid="list-business-btn"
          >
            List Your Business
          </Button>
        </div>
      </section>

      {/* Business Detail Modal */}
      <BusinessDetailModal
        business={selectedBusiness}
        open={!!selectedBusiness}
        onClose={closeBusinessModal}
        isSaved={selectedBusiness ? savedBusinessIds.includes(selectedBusiness.id) : false}
        onFollow={handleFollowBusiness}
      />
    </div>
  );
};

const BusinessCard = ({ business, onClick, isSaved, onFollow }) => {
  const [logoFailed, setLogoFailed] = useState(false);
  const logoUrl = getLogoUrl(business.logo);
  const hasLogo = logoUrl && !logoFailed;
  const subcategories = getBusinessSubcategories(business);
  const locations = getBusinessLocations(business);
  const locationLabel = locations.slice(0, 2).join(", ");

  useEffect(() => {
    setLogoFailed(false);
  }, [business.logo]);

  return (
    <div
      onClick={onClick}
      className="bg-white border border-gray-100 rounded-2xl overflow-hidden cursor-pointer hover:shadow-xl transition-all duration-300 group"
      data-testid={`business-card-${business.id}`}
    >
      <div className="relative">
        {business.business_type && (
          <Badge className="absolute top-3 right-3 z-10 bg-[#1a1a1a] text-white text-xs font-normal rounded-full px-3">
            {business.business_type}
          </Badge>
        )}
        <div
          className="relative flex items-center justify-center"
          style={{
            width: '100%',
            height: '0',
            paddingBottom: '64.5%',
            backgroundImage: `url(${DEFAULT_BG_IMAGE})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          {hasLogo ? (
            <div className="absolute inset-0 flex items-center justify-center p-6 bg-white">
              <img src={logoUrl} alt={business.business_name} onError={() => setLogoFailed(true)} className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-300" style={{ maxHeight: '80%', maxWidth: '80%' }} />
            </div>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center p-4">
              <span className="text-xl font-bold text-center text-slate-800 px-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {business.business_name || "Unnamed Business"}
              </span>
            </div>
          )}
        </div>
      </div>
      <div className="p-5">
        <div className="flex items-start justify-between gap-3 mb-1">
          <h3 className="font-bold text-base text-[#1a1a1a] group-hover:text-[#42D78A] transition-colors" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {business.business_name || "Unnamed Business"}
          </h3>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onFollow(business.id); }}
            className={`w-9 h-9 rounded-full border flex items-center justify-center transition-colors ${isSaved ? "bg-[#42D78A] border-[#42D78A] text-[#1a1a1a]" : "bg-white border-gray-200 text-gray-500 hover:border-[#42D78A] hover:text-[#1a1a1a]"}`}
            aria-label={isSaved ? "Remove saved business" : "Save business"}
            data-testid={`follow-business-${business.id}`}
          >
            <Heart className={`w-4 h-4 ${isSaved ? "fill-current" : ""}`} />
          </button>
        </div>
        <p className="text-gray-500 text-sm mb-3 line-clamp-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>
          {business.business_description || business.business_type || ""}
        </p>
        {locationLabel && (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-500" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            <MapPin className="w-3 h-3" /> {locationLabel}{locations.length > 2 ? ` +${locations.length - 2}` : ""}
          </span>
        )}
        {subcategories.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {subcategories.slice(0, 3).map((category) => (
              <span key={category} className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                {category}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export const BusinessDetailModal = ({ business, open, onClose, isSaved, onFollow }) => {
  const [logoFailed, setLogoFailed] = useState(false);
  const defaultLocationIndex = useMemo(() => getDefaultLocationIndex(business), [business]);
  const [selectedLocationIndex, setSelectedLocationIndex] = useState(defaultLocationIndex);
  const [selectedAssociatedMarket, setSelectedAssociatedMarket] = useState(null);

  useEffect(() => {
    setLogoFailed(false);
  }, [business?.id, business?.logo]);

  useEffect(() => {
    setSelectedLocationIndex(defaultLocationIndex);
    setSelectedAssociatedMarket(null);
  }, [defaultLocationIndex]);

  if (!business) return null;

  const logoUrl = getLogoUrl(business.logo);
  const hasLogo = logoUrl && !logoFailed;
  const subcategories = getBusinessSubcategories(business);
  const locationEntries = getBusinessLocationEntries(business);
  const associatedMarkets = Array.isArray(business.associated_markets) ? business.associated_markets : [];
  const selectedLocation = selectedLocationIndex === null
    ? null
    : locationEntries[Math.min(selectedLocationIndex, Math.max(locationEntries.length - 1, 0))];

  return (
    <>
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent className="w-[calc(100vw-1rem)] max-w-2xl max-h-[calc(100dvh-1rem)] overflow-y-auto p-0 rounded-2xl sm:w-full">
        <DialogTitle className="sr-only">{business.business_name} Details</DialogTitle>
        <DialogDescription className="sr-only">Detailed information about {business.business_name}</DialogDescription>
        <button onClick={onClose} className="absolute right-3 top-3 sm:right-4 sm:top-4 z-10 p-1 bg-white/80 backdrop-blur rounded-full" data-testid="close-modal-btn">
          <X className="w-5 h-5" />
        </button>

        <div className="p-4 sm:p-8">
          {/* Logo */}
          <div
            className="relative flex aspect-[1.55/1] w-full max-w-[465px] items-center justify-center mb-6 sm:mb-8 mx-auto rounded-xl overflow-hidden"
            style={{
              backgroundImage: `url(${DEFAULT_BG_IMAGE})`,
              backgroundSize: 'cover', backgroundPosition: 'center'
            }}
          >
            {hasLogo ? (
              <div className="absolute inset-0 flex items-center justify-center p-8 bg-white">
                <img src={logoUrl} alt={business.business_name} onError={() => setLogoFailed(true)} className="max-h-full max-w-full object-contain" />
              </div>
            ) : (
              <div className="flex items-center justify-center p-4">
                <span className="text-2xl md:text-3xl font-bold text-center text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {business.business_name}
                </span>
              </div>
            )}
          </div>

          <div className="space-y-5">
            <div>
              <p className="text-xs tracking-widest text-gray-400 mb-1 uppercase" style={{ fontFamily: "'Montserrat', sans-serif" }}>Business Name</p>
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                <p className="text-xl font-bold text-[#1a1a1a] break-words" style={{ fontFamily: "'Outfit', sans-serif" }}>{business.business_name}</p>
                <button
                  type="button"
                  onClick={() => onFollow(business.id)}
                  className={`shrink-0 inline-flex w-fit items-center gap-2 rounded-full border px-4 py-2 text-sm font-bold transition-colors ${isSaved ? "bg-[#42D78A] border-[#42D78A] text-[#1a1a1a]" : "bg-white border-gray-200 text-gray-600 hover:border-[#42D78A] hover:text-[#1a1a1a]"}`}
                  aria-label={isSaved ? "Remove saved business" : "Save business"}
                  data-testid={`modal-follow-business-${business.id}`}
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  <Heart className={`w-4 h-4 ${isSaved ? "fill-current" : ""}`} />
                  {isSaved ? "Saved" : "Save"}
                </button>
              </div>
            </div>
            {business.business_type && (
              <div>
                <p className="text-xs tracking-widest text-gray-400 mb-1 uppercase" style={{ fontFamily: "'Montserrat', sans-serif" }}>Main Category</p>
                <Badge className="bg-[#1a1a1a] text-white rounded-full px-4 py-1">{business.business_type}</Badge>
              </div>
            )}
            {subcategories.length > 0 && (
              <div>
                <p className="text-xs tracking-widest text-gray-400 mb-2 uppercase" style={{ fontFamily: "'Montserrat', sans-serif" }}>Subcategories</p>
                <div className="flex flex-wrap gap-2">
                  {subcategories.map((category) => (
                    <Badge key={category} className="bg-gray-100 text-gray-700 border border-gray-200 rounded-full px-3 py-1">
                      {category}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            {(business.instagram || business.facebook || business.tiktok) && (
              <div>
                <p className="text-xs tracking-widest text-gray-400 mb-2 uppercase" style={{ fontFamily: "'Montserrat', sans-serif" }}>Social Media</p>
                <SocialMediaIcons instagram={business.instagram} facebook={business.facebook} tiktok={business.tiktok} />
              </div>
            )}
            {business.hours && (
              <div>
                <p className="text-xs tracking-widest text-gray-400 mb-1 uppercase" style={{ fontFamily: "'Montserrat', sans-serif" }}>Hours</p>
                <p className="text-sm font-medium whitespace-pre-line">{formatDirectoryHours(business.hours)}</p>
              </div>
            )}
            {business.business_description && (
              <div>
                <p className="text-xs tracking-widest text-gray-400 mb-1 uppercase" style={{ fontFamily: "'Montserrat', sans-serif" }}>About</p>
                <p className="text-sm text-gray-600 leading-relaxed">{business.business_description}</p>
              </div>
            )}
            {locationEntries.length > 0 && (
              <div>
                <p className="text-xs tracking-widest text-gray-400 mb-1 uppercase" style={{ fontFamily: "'Montserrat', sans-serif" }}>Locations</p>
                <div className="mb-2 flex flex-wrap gap-2">
                  {locationEntries.map((entry, index) => {
                    const isSelected = selectedLocationIndex === index;
                    return (
                      <button
                        key={`${entry.location}-${index}`}
                        type="button"
                        onClick={() => setSelectedLocationIndex(index)}
                        className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                          isSelected
                            ? "bg-gray-100 text-[#1a1a1a]"
                            : "bg-white text-gray-600 ring-1 ring-gray-200 hover:bg-gray-50 hover:text-[#1a1a1a]"
                        }`}
                      >
                        <MapPin className="w-4 h-4 text-[#42D78A]" />
                        {entry.location || `Location ${index + 1}`}
                      </button>
                    );
                  })}
                </div>
                <p className="mb-3 text-sm text-gray-500">Click a location to view the address and map.</p>
                {selectedLocation && (
                  selectedLocation.address ? (
                    <div className="overflow-hidden rounded-xl border border-gray-100">
                      <div className="relative aspect-[4/3] w-full bg-gray-100">
                        <iframe
                          title={`${business.business_name} map`}
                          src={`https://www.google.com/maps?q=${encodeURIComponent(getMapQuery(selectedLocation))}&output=embed`}
                          className="absolute inset-0 h-full w-full border-0"
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                        />
                        <div className="absolute bottom-3 left-3 right-3 rounded-lg bg-white/95 p-3 shadow-sm">
                          <p className="text-sm font-bold text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                            {selectedLocation.location || "Business Location"}
                          </p>
                          <p className="mt-1 whitespace-pre-line text-xs font-medium text-gray-600">{selectedLocation.address}</p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">Address not listed publicly.</p>
                  )
                )}
              </div>
            )}
            {associatedMarkets.length > 0 && (
              <div className="pt-2">
                <p className="text-xs tracking-widest text-gray-400 mb-3 uppercase" style={{ fontFamily: "'Montserrat', sans-serif" }}>Markets</p>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {associatedMarkets.map((market) => (
                    <button
                      key={market.id}
                      type="button"
                      onClick={() => setSelectedAssociatedMarket(market)}
                      className="flex items-center gap-3 rounded-2xl border border-gray-100 bg-gray-50 p-3 text-left transition-all hover:-translate-y-0.5 hover:border-[#42D78A]/50 hover:bg-white hover:shadow-sm"
                      data-testid={`associated-market-${market.id}`}
                    >
                      {getLogoUrl(market.logo) ? (
                        <img src={getLogoUrl(market.logo)} alt={`${market.market_name} logo`} className="h-12 w-12 shrink-0 rounded-xl object-cover bg-white" />
                      ) : (
                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#42D78A]/15 text-[#157347]">
                          <Store className="h-5 w-5" />
                        </div>
                      )}
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-bold text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>{market.market_name || "Unnamed Market"}</span>
                        <span className="mt-0.5 block truncate text-xs text-gray-500">{[market.location, market.address].filter(Boolean).join(" · ") || "View market profile"}</span>
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {business.website && (
              <Button
                className="w-full bg-[#1a1a1a] hover:bg-black text-white rounded-full py-5 mt-4 font-bold"
                onClick={() => window.open(normalizeUrl(business.website), "_blank")}
                style={{ fontFamily: "'Outfit', sans-serif" }}
                data-testid="visit-website-btn"
              >
                <Globe className="w-4 h-4 mr-2" />
                Visit Website
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
    <AssociatedMarketModal
      market={selectedAssociatedMarket}
      open={!!selectedAssociatedMarket}
      onClose={() => setSelectedAssociatedMarket(null)}
    />
    </>
  );
};

const AssociatedMarketModal = ({ market, open, onClose }) => {
  const [logoFailed, setLogoFailed] = useState(false);

  useEffect(() => {
    setLogoFailed(false);
  }, [market?.id, market?.logo]);

  if (!market) return null;

  const logoUrl = getLogoUrl(market.logo);
  const hasLogo = logoUrl && !logoFailed;
  const season = formatMarketSeason(market);
  const hoursDisplay = typeof market.hours === "object" ? formatHoursDisplay(market.hours) : market.hours;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent className="w-[calc(100vw-1rem)] max-w-2xl max-h-[calc(100dvh-1rem)] overflow-y-auto p-0 rounded-2xl sm:w-full">
        <DialogTitle className="sr-only">{market.market_name} Details</DialogTitle>
        <DialogDescription className="sr-only">Detailed information about {market.market_name}</DialogDescription>
        <button onClick={onClose} className="absolute right-3 top-3 sm:right-4 sm:top-4 z-10 p-1 bg-white/80 backdrop-blur rounded-full" data-testid="close-associated-market-modal-btn">
          <X className="w-5 h-5" />
        </button>

        <div className="p-4 sm:p-8">
          <div className="relative flex aspect-[1.55/1] w-full max-w-[465px] items-center justify-center mb-6 sm:mb-8 mx-auto rounded-xl overflow-hidden" style={{ backgroundImage: `url(${DEFAULT_BG_IMAGE})`, backgroundSize: "cover", backgroundPosition: "center" }}>
            {hasLogo ? (
              <div className="absolute inset-0 flex items-center justify-center p-8 bg-white">
                <img src={logoUrl} alt={market.market_name} onError={() => setLogoFailed(true)} className="max-h-full max-w-full object-contain" />
              </div>
            ) : (
              <div className="flex items-center justify-center p-4">
                <span className="text-2xl md:text-3xl font-bold text-center text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>{market.market_name}</span>
              </div>
            )}
          </div>

          <div className="space-y-5">
            <div>
              <p className="text-xs tracking-widest text-gray-400 mb-1 uppercase" style={{ fontFamily: "'Montserrat', sans-serif" }}>Market Name</p>
              <p className="text-xl font-bold text-[#1a1a1a] break-words" style={{ fontFamily: "'Outfit', sans-serif" }}>{market.market_name}</p>
            </div>
            {season && <div><p className="text-xs tracking-widest text-gray-400 mb-1 uppercase">Season</p><p className="text-sm font-medium">{season}</p></div>}
            {hoursDisplay && <div><p className="text-xs tracking-widest text-gray-400 mb-1 uppercase">Hours</p><p className="text-sm font-medium whitespace-pre-line">{hoursDisplay}</p></div>}
            {market.address && <div><p className="text-xs tracking-widest text-gray-400 mb-1 uppercase">Address</p><p className="text-sm font-medium flex items-start gap-1 break-words"><MapPin className="w-4 h-4 shrink-0 text-[#42D78A]" />{market.address}</p></div>}
            {(market.instagram || market.facebook || market.tiktok) && (
              <div>
                <p className="text-xs tracking-widest text-gray-400 mb-2 uppercase">Social Media</p>
                <SocialMediaIcons instagram={market.instagram} facebook={market.facebook} tiktok={market.tiktok} />
              </div>
            )}
            {market.description && (
              <div>
                <p className="text-xs tracking-widest text-gray-400 mb-1 uppercase">About</p>
                <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">{market.description}</p>
              </div>
            )}
            {market.website && (
              <Button
                className="w-full bg-[#1a1a1a] hover:bg-black text-white rounded-full py-5 mt-4 font-bold"
                onClick={() => window.open(normalizeUrl(market.website), "_blank")}
                style={{ fontFamily: "'Outfit', sans-serif" }}
                data-testid="visit-associated-market-website-btn"
              >
                <Globe className="w-4 h-4 mr-2" />
                Visit Website
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DirectoryPage;
